"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsAdManagement = exports.googleAdsAdManagementArgsSchema = exports.GoogleAdsAdAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Ad ----------
// Base fields common to all ad types
var baseAdFields = {
    adGroupId: zod_1.z.string().describe('The ID of the ad group where the ad will be created'),
    finalUrls: zod_1.z
        .array(zod_1.z.string().url())
        .min(1)
        .describe('Final URLs that users will land on when clicking the ad'),
    path1: zod_1.z
        .string()
        .max(15)
        .nullish()
        .describe('Optional display path 1. CRITICAL: Maximum 15 characters (including spaces). Count characters BEFORE calling. Must be provided by user or left null. Never auto-generate. Examples: "products" (8 chars), "sale" (4 chars), "new-arrivals" (12 chars).'),
    path2: zod_1.z
        .string()
        .max(15)
        .nullish()
        .describe('Optional display path 2. CRITICAL: Maximum 15 characters (including spaces). Count characters BEFORE calling. Must be provided by user or left null. Never auto-generate. Examples: "shop-now" (8 chars), "deals" (5 chars), "2024" (4 chars).'),
    status: zod_1.z
        .enum(['ENABLED', 'PAUSED'])
        .default('PAUSED')
        .describe('Initial status for the ad. Defaults to PAUSED for safety.'),
};
// Responsive Search Ad
var responsiveSearchAdSchema = zod_1.z.object(__assign(__assign({}, baseAdFields), { adType: zod_1.z.literal('RESPONSIVE_SEARCH_AD'), headlines: zod_1.z
        .array(zod_1.z.string().max(30, 'Each headline must be 30 characters or less'))
        .min(3, 'At least 3 headlines required')
        .max(15, 'Maximum 15 headlines allowed')
        .describe('Headlines for the Responsive Search Ad. CRITICAL: Requires 3-15 headlines, each with maximum 30 characters (including spaces). Count characters in EACH headline BEFORE calling - must be 30 chars or less. Truncate or rewrite any that exceed this limit. Examples: "Shop Latest Collection" (22 chars), "Free Shipping Today" (19 chars), "Save Up To 50% Off" (18 chars).'), descriptions: zod_1.z
        .array(zod_1.z.string().max(90, 'Each description must be 90 characters or less'))
        .min(2, 'At least 2 descriptions required')
        .max(4, 'Maximum 4 descriptions allowed')
        .describe('Descriptions for the Responsive Search Ad. CRITICAL: Requires 2-4 descriptions, each with maximum 90 characters (including spaces). Count characters in EACH description BEFORE calling - must be 90 chars or less. Truncate or rewrite any that exceed this limit. Examples: "Discover premium products with free shipping on orders over $50" (64 chars), "Shop now and save big on our latest arrivals" (45 chars).') }));
// Expanded Text Ad
var expandedTextAdSchema = zod_1.z.object(__assign(__assign({}, baseAdFields), { adType: zod_1.z.literal('EXPANDED_TEXT_AD'), headlines: zod_1.z
        .array(zod_1.z.string().max(30, 'Each headline must be 30 characters or less'))
        .length(3, 'Exactly 3 headlines required for Expanded Text Ads')
        .describe('Headlines for the Expanded Text Ad. CRITICAL: Requires exactly 3 headlines, each with maximum 30 characters (including spaces). Count characters in EACH headline BEFORE calling - must be 30 chars or less. Truncate or rewrite any that exceed this limit. Examples: "Premium Quality Products" (24 chars), "Best Prices Guaranteed" (22 chars), "Shop Now & Save 40%" (19 chars).'), descriptions: zod_1.z
        .array(zod_1.z.string().max(90, 'Each description must be 90 characters or less'))
        .length(2, 'Exactly 2 descriptions required for Expanded Text Ads')
        .describe('Descriptions for the Expanded Text Ad. CRITICAL: Requires exactly 2 descriptions, each with maximum 90 characters (including spaces). Count characters in EACH description BEFORE calling - must be 90 chars or less. Truncate or rewrite any that exceed this limit. Examples: "Browse our extensive catalog of premium products with fast, free delivery" (73 chars), "Limited time offer - save up to 40% on select items" (52 chars).') }));
// Image Ad
var imageAdSchema = zod_1.z.object(__assign(__assign({}, baseAdFields), { adType: zod_1.z.literal('IMAGE_AD'), imageUrl: zod_1.z
        .string()
        .url()
        .describe('CRITICAL: URL of the image to use for the ad. Image MUST meet EXACT specifications: Dimensions must be EXACTLY one of: 200x200, 240x400, 250x250, 250x360, 300x250, 336x280, or 580x400 pixels. File must be PNG or JPG format only and under 150KB. Images with any other dimensions, format, or file size will be REJECTED by Google Ads API.'), displayUrl: zod_1.z
        .string()
        .describe('REQUIRED: Display URL shown in the ad (e.g., www.example.com). This is the URL displayed to users, separate from the final landing page URL.'), name: zod_1.z
        .string()
        .max(255)
        .nullish()
        .describe('Optional name for the ad (max 255 characters). Used for internal reference.') }));
var googleAdsCreateAdInputSchema = zod_1.z
    .discriminatedUnion('adType', [responsiveSearchAdSchema, expandedTextAdSchema, imageAdSchema])
    .describe('Create a new Google Ads ad in the specified ad group.');
var googleAdsCreateAdResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    adId: zod_1.z.string().nullish(),
    resourceName: zod_1.z.string().nullish(),
    message: zod_1.z.string().nullish(),
});
var googleAdsCreateAdResultSchema = googleAdsCreateAdResponseSchema;
// ---------- Update Ad ----------
var googleAdsUpdateAdInputSchema = zod_1.z
    .object({
    adGroupAdResourceName: zod_1.z
        .string()
        .regex(/^customers\/[\d-]+\/adGroupAds\/\d+~\d+$/, 'Must be in format: customers/{customer_id}/adGroupAds/{ad_group_id}~{ad_id} (uses tilde ~ separator). Customer ID can contain hyphens (e.g., 123-456-7890).')
        .describe('REQUIRED: The full resource name of the ad to update. Format: customers/{customer_id}/adGroupAds/{ad_group_id}~{ad_id}. Use tilde (~) to separate ad_group_id and ad_id, NOT underscore. Customer ID can be numeric (e.g., 9517443549) or hyphenated (e.g., 951-744-3549). Example: customers/9517443549/adGroupAds/189185188536~7606277225'),
    status: zod_1.z.enum(['ENABLED', 'PAUSED', 'REMOVED']).nullish().describe('New status for the ad'),
    finalUrls: zod_1.z.array(zod_1.z.string().url()).nullish().describe('New final URLs for the ad'),
})
    .describe('Update an existing Google Ads ad.');
var googleAdsUpdateAdResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    resourceName: zod_1.z.string().nullish(),
    message: zod_1.z.string().nullish(),
});
var googleAdsUpdateAdResultSchema = googleAdsUpdateAdResponseSchema;
// ==========================
// Compound Tool: googleAdsAdManagement
// ==========================
exports.GoogleAdsAdAction = zod_1.z.enum(['createAd', 'updateAd']);
exports.googleAdsAdManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createAd'), params: googleAdsCreateAdInputSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateAd'), params: googleAdsUpdateAdInputSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var GoogleAdsAdManagementAllResultsSchema = zod_1.z.union([
    googleAdsCreateAdResultSchema,
    googleAdsUpdateAdResultSchema,
]);
var googleAdsAdManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createAd' }, schema: googleAdsCreateAdResultSchema },
        { when: { 'action.name': 'updateAd' }, schema: googleAdsUpdateAdResultSchema },
    ],
    defaultSchema: GoogleAdsAdManagementAllResultsSchema,
});
exports.googleAdsAdManagement = (0, common_1.createToolMetadata)({
    id: 'googleAdsAdManagement',
    displayName: 'Google Ads Ad Management',
    displayDescription: 'Create and update Google Ads ads',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsAdManagementArgsSchema,
}, googleAdsAdManagementResultSchemas);
